import type { RefObject } from "react";

import { Button } from "@/components/Button";
import { SearchField } from "@/components/fields/SearchField";
import { Figures } from "@/components/Figures";
import { SelectField } from "@/components/fields/SelectField";
import segmented from "@/components/Segmented.module.css";
import { STAGE_FILTERS } from "@/lib/stages";

import styles from "./Register.module.css";

export type RegisterSort = "activity" | "created" | "volume";

export interface CustomerChoice {
  id: string;
  displayName: string;
}

// The words of the column heads the select stands in for on a phone —
// „Zuletzt", not „Zuletzt bearbeitet", which a third of a phone's width
// cut to „Zuletzt bearbei" (fix round 3).
const SORTS: readonly { value: RegisterSort; label: string }[] = [
  { value: "activity", label: "Zuletzt" },
  { value: "created", label: "Angelegt" },
  { value: "volume", label: "Volumen" },
];

const STAGE_OPTIONS = [
  { value: "", label: "Alle" },
  ...STAGE_FILTERS.map((filter) => ({ value: filter.value, label: filter.label })),
];

function isSort(value: string): value is RegisterSort {
  return SORTS.some((sort) => sort.value === value);
}

interface RegisterToolbarProps {
  search: string;
  onSearch: (value: string) => void;
  searchRef: RefObject<HTMLInputElement | null>;
  stageFilter: string;
  onStageFilter: (value: string) => void;
  customer: CustomerChoice | null;
  customers: readonly CustomerChoice[];
  onCustomer: (choice: CustomerChoice | null) => void;
  sort: RegisterSort;
  onSort: (value: RegisterSort) => void;
  showFinished: boolean;
  onToggleFinished: () => void;
  /** The line under the tools: how many, or what just happened. Never empty while there is a page. */
  status: string;
  /**
   * Fix round 1, M4: when the visible content below already spells the same
   * sentence out in full (the „Vorgänge nicht erreichbar" box, title and
   * body), the status line still carries it — a screen reader needs the
   * announcement — but stops printing it a second time for sighted eyes.
   */
  statusVisuallyHidden?: boolean;
  /**
   * „Filter zurücksetzen" beside the count (UX gate round 4, #9) — while a
   * search, a filter or „Beendete zeigen" narrows the list. `undefined`
   * where there is nothing to reset, or where the frame below offers the
   * same with more words (no hits).
   */
  onReset?: () => void;
}

/**
 * Search, stage, customer, the finished switch — one row on a wide frame,
 * exactly two below 60 rem (fix round 3), with the stage never narrower
 * than its longest option (UX gate round 3, #7); three only below 22 rem,
 * where the stage takes a row of its own rather than cut the search's
 * placeholder. Then the status line across the whole width (UX ruling 4,
 * F-13/F-14). The sort select is
 * the phone's way to sort; above 720 px the column heads do it and the
 * select is hidden. The status line is the live region: it says the number
 * of hits after a search and a refused stage gesture in its place.
 */
export function RegisterToolbar({
  search,
  onSearch,
  searchRef,
  stageFilter,
  onStageFilter,
  customer,
  customers,
  onCustomer,
  sort,
  onSort,
  showFinished,
  onToggleFinished,
  status,
  statusVisuallyHidden = false,
  onReset,
}: RegisterToolbarProps) {
  return (
    <div className={styles.toolbar}>
      <div className={styles.tools} role="search">
        <div className={styles.searchField}>
          <SearchField
            id="register-search"
            label="Suchen"
            value={search}
            onChange={onSearch}
            // Fix round 4, M6: whole on a phone too (165 px of text there;
            // the long form was cut to „Kunde, Vorgangsnumm…"). The frame
            // spells the four fields out when a search finds nothing.
            placeholder="Kunde, Nr., E-Mail, Titel"
            inputRef={searchRef}
          />
        </div>
        <div className={styles.stageField}>
          <SelectField id="register-stage" label="Stadium" value={stageFilter} options={STAGE_OPTIONS} onChange={onStageFilter} />
        </div>
        <div className={styles.customerField}>
          <SelectField
            id="register-customer"
            label="Kunde"
            value={customer?.id ?? ""}
            options={[
              { value: "", label: "Alle Kunden" },
              ...customers.map((choice) => ({ value: choice.id, label: choice.displayName })),
            ]}
            onChange={(id) => onCustomer(id === "" ? null : (customers.find((choice) => choice.id === id) ?? null))}
          />
        </div>
        <div className={styles.sortField}>
          <SelectField
            id="register-sort"
            label="Sortiert nach"
            value={sort}
            options={SORTS}
            onChange={(value) => {
              if (isSort(value)) onSort(value);
            }}
          />
        </div>
        <div className={`${segmented.group} ${styles.finished}`}>
          {/* M5: matches the fields' own control height, so the row does not
              show one visibly shorter control beside three taller ones. */}
          {/* The whole label at every width (screen check S-12): „Beendete"
              alone read as a state, not as what the switch does. On a very
              narrow frame the second word moves under the first instead of
              going. `aria-label` is exactly the visible text: the name stays
              „Beendete zeigen" however a browser joins a word in a block of
              its own. */}
          <button
            type="button"
            className={`${segmented.segment} ${styles.finishedToggle}`}
            aria-pressed={showFinished}
            aria-label="Beendete zeigen"
            onClick={onToggleFinished}
          >
            Beendete<span className={styles.toggleRest}> zeigen</span>
          </button>
        </div>
      </div>
      {/* Present before its text changes, so a new count is announced. The
          count in the figure face (UX gate round 1, #13). The way back to the
          whole list stands beside it, outside the live region (UX gate
          round 4, #9): the count says what narrows the list, the button
          undoes it — one click, not three fields. */}
      <div className={styles.statusRow}>
        <p role="status" className={`${styles.status} ${statusVisuallyHidden ? "visually-hidden" : ""}`}>
          <Figures text={status} />
        </p>
        {onReset !== undefined ? (
          <Button variant="quiet" flush className={styles.reset} onClick={onReset}>
            Filter zurücksetzen
          </Button>
        ) : null}
      </div>
    </div>
  );
}
